import { DomainInfo } from '@/types/tenant';

/**
 * Get the base domain from environment or default
 */
export function getBaseDomain(): string {
  return import.meta.env.VITE_BASE_DOMAIN || 'florentin.dev';
}

/**
 * Parse hostname to extract domain information
 */
export function parseDomain(hostname: string): DomainInfo {
  const baseDomain = getBaseDomain();
  const normalizedHost = hostname.toLowerCase();
  const providerSuffix = import.meta.env.VITE_PROVIDER_DOMAIN_SUFFIX?.toLowerCase();
  const matchesProviderSuffix =
    Boolean(providerSuffix) && normalizedHost.endsWith(`.${providerSuffix}`);
  const isLocalhost =
    normalizedHost === 'localhost' ||
    normalizedHost === '127.0.0.1' ||
    normalizedHost.endsWith('.localhost') ||
    normalizedHost.endsWith('.test') ||
    (import.meta.env.DEV && matchesProviderSuffix);
  
  let subdomain: string | null = null;
  let isCustomDomain = false;

  if (isLocalhost) {
    // Handle localhost-style development
    // Format: subdomain.localhost:3000, subdomain.test:3000, subdomain.{suffix}:3000, or localhost:3000
    const parts = hostname.split('.');
    if (parts.length > 1 && parts[0] !== 'www') {
      subdomain = parts[0];
    } else {
      // Use development provider from env
      subdomain = import.meta.env.VITE_DEVELOPMENT_PROVIDER_SLUG || 'demo';
    }
  } else if (hostname.includes(baseDomain)) {
    // Handle Florentin subdomains (e.g., acme.florentin.dev)
    const parts = hostname.split('.');
    const baseParts = baseDomain.split('.').length;
    
    if (parts.length > baseParts) {
      // Extract subdomain
      subdomain = parts[0];
      if (subdomain === 'www') {
        subdomain = null; // www is not a provider subdomain
      }
    }
  } else {
    // Custom domain (e.g., api.acme.com)
    isCustomDomain = true;
  }

  return {
    hostname,
    subdomain,
    isCustomDomain,
    isLocalhost,
    baseDomain,
  };
}

/**
 * Get the current domain info from window.location
 */
export function getCurrentDomain(): DomainInfo {
  return parseDomain(window.location.hostname);
}

/**
 * Build a URL for the current tenant
 */
export function buildTenantUrl(path: string, domain?: DomainInfo): string {
  const domainInfo = domain || getCurrentDomain();
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : '';
  
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  return `${protocol}//${domainInfo.hostname}${port}${normalizedPath}`;
}

/**
 * Check if we should enable multi-tenant features
 */
export function isMultiTenantEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_CUSTOM_DOMAINS === 'true';
}

/**
 * Get provider slug from domain info
 */
export function getProviderSlugFromDomain(domain: DomainInfo): string | null {
  if (domain.isCustomDomain) {
    // For custom domains, we'll need to fetch from API
    return null;
  }
  
  return domain.subdomain;
}

/**
 * Development helper to simulate different providers
 */
export function getDevProviderSlug(): string {
  // Check URL params first for testing
  const params = new URLSearchParams(window.location.search);
  const devProvider = params.get('provider');
  
  if (devProvider) {
    return devProvider;
  }
  
  // Fall back to environment variable
  return import.meta.env.VITE_DEVELOPMENT_PROVIDER_SLUG || 'demo';
}

/**
 * Validate if a domain is allowed
 */
export function isValidDomain(hostname: string): boolean {
  const domain = parseDomain(hostname);
  
  // Always allow localhost
  if (domain.isLocalhost) {
    return true;
  }
  
  // Allow base domain and subdomains
  if (hostname.includes(domain.baseDomain)) {
    return true;
  }
  
  // Custom domains need API validation
  if (domain.isCustomDomain && isMultiTenantEnabled()) {
    return true; // Will be validated by API
  }
  
  return false;
}
